/**
 * Admin API Utility
 * Section 6.1 - Centralized API calls for admin panel
 */

import { getAuthHeaders, clearToken } from './adminAuth';

const API_BASE = '/api/admin';

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  // Handle 401 - clear token and redirect to login
  if (response.status === 401) {
    clearToken();
    window.location.href = '/admin';
    throw new Error('Session expired');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'API request failed');
  }

  return data;
}

/**
 * Dashboard stats
 */
export async function getStats() {
  return apiRequest('/stats');
}

/**
 * Vehicle database operations
 */
export const vehicles = {
  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return apiRequest(`/vehicles?${params}`);
  },

  async getById(id) {
    return apiRequest(`/vehicles?id=${encodeURIComponent(id)}`);
  },

  async create(vehicle) {
    return apiRequest('/vehicles', {
      method: 'POST',
      body: JSON.stringify(vehicle)
    });
  },

  async update(id, changes) {
    return apiRequest(`/vehicles?id=${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ id, ...changes })
    });
  },

  async delete(id) {
    return apiRequest(`/vehicles?id=${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }
};

/**
 * Pricing configuration operations
 */
export const pricing = {
  async get() {
    return apiRequest('/pricing');
  },

  async update(config) {
    return apiRequest('/pricing', {
      method: 'PUT',
      body: JSON.stringify(config)
    });
  }
};

/**
 * Override operations
 */
export const overrides = {
  async getAll() {
    return apiRequest('/overrides');
  },

  async getByVehicleId(vehicleId) {
    return apiRequest(`/overrides?vehicleId=${encodeURIComponent(vehicleId)}`);
  },

  async create(override) {
    return apiRequest('/overrides', {
      method: 'POST',
      body: JSON.stringify(override)
    });
  },

  async update(vehicleId, override) {
    return apiRequest(`/overrides?vehicleId=${encodeURIComponent(vehicleId)}`, {
      method: 'PUT',
      body: JSON.stringify({ vehicle_id: vehicleId, ...override })
    });
  },

  async delete(vehicleId) {
    return apiRequest(`/overrides?vehicleId=${encodeURIComponent(vehicleId)}`, {
      method: 'DELETE'
    });
  }
};

/**
 * Error logs operations
 */
export const logs = {
  async get(filters = {}) {
    const params = new URLSearchParams(filters);
    return apiRequest(`/logs?${params}`);
  },

  async create(log) {
    return apiRequest('/logs', {
      method: 'POST',
      body: JSON.stringify(log)
    });
  },

  async delete(id) {
    return apiRequest(`/logs?id=${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  },

  async clear(timeRange) {
    const params = timeRange ? `?timeRange=${timeRange}` : '';
    return apiRequest(`/logs${params}`, {
      method: 'DELETE'
    });
  }
};

/**
 * Import/Export operations
 */
export const dataIO = {
  async exportData(format = 'json', filters = {}) {
    const params = new URLSearchParams({ format, ...filters });
    return apiRequest(`/export?${params}`);
  },

  async importData(data, dryRun = true) {
    return apiRequest('/import', {
      method: 'POST',
      body: JSON.stringify({ data, dryRun })
    });
  },

  async validateImport(data) {
    return apiRequest('/import/validate', {
      method: 'POST',
      body: JSON.stringify({ data })
    });
  }
};

const adminApi = {
  getStats,
  vehicles,
  pricing,
  overrides,
  logs,
  dataIO
};

export default adminApi;
